import type { CatalogEntry } from '../types';

/**
 * Internal backfill + ops-trigger (feature `22`) error codes + catalog. Both
 * routes below live in `InternalBackfillModule` and share its
 * `CronValidationGuard` (shared `x-cron-validation-token` secret, same as
 * `POST /ai/generate-content` and `POST /reports/warmup/resync`; throws
 * `ErrorCode.Auth.CronAccess.InvalidToken` on rejection, not a code from this
 * file):
 *
 * - `POST /internal/backfill/:table` — backfills a derived-data table
 *   (`dns-security` | `warmup-score` | `domain-age`) for accounts migrated
 *   straight into MySQL. `dns-security` and `warmup-score` replace the old
 *   `backfill-derived-data` CLI script; `domain-age` was added directly here.
 * - `POST /internal/mailbox-session/trigger` — enqueues one `reconcile-one`
 *   job per account so warmup-worker seeds (or reports why it can't) that
 *   account's mailbox session immediately, instead of waiting for the 15-min
 *   reconciler sweep. Same request/response shape family as the backfill
 *   route above (explicit `accountIds`, or auto-discover via `maxAccounts`/
 *   `afterId`). Fire-and-forget per id — the seed/no-seed outcome itself is
 *   only visible in warmup-worker's own logs
 *   (`MailboxSessionReconcilerService.seedForAccount`), because eligibility
 *   (connected + an active warmup status) is evaluated asynchronously off
 *   the request.
 *
 * Code shape is `22AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `accountIds`/`maxAccounts`/`afterId` validation on BOTH routes is covered
 * by the global `ValidationPipe` on their request DTOs and resolves to the
 * generic `000001` uncategorized-validation code — no custom code needed for
 * either endpoint's input validation.
 *
 * `220201` (`MailboxSessionTrigger.InvalidAccountId`) is RETIRED — do not
 * reuse. It belonged to this route's original single-account
 * `:emailAccountId` route-param shape (`ParseIntPipe` alone accepts 0/
 * negative integers), before the route moved to the batched
 * `accountIds[]`/`maxAccounts`/`afterId` body shape above, whose validation
 * is DTO-level and needs no custom code. `220201` may still appear in logs
 * from that shape's brief life in this branch.
 *
 * - `POST /internal/mailbox-activation/:accountId` — stores an imported OAuth
 *   access/refresh token pair on an already-connected mailbox, enables warmup
 *   (normal gates), runs the three derived-data backfills for it and enqueues a
 *   delayed `reconcile-one`. A failing step aborts the call with that step's
 *   own code — `enableWarmup`'s gate codes (`0203xx`, e.g. `020007`) pass
 *   through untouched, so the code alone names the step that failed.
 *
 * Endpoint → `AA` map: runBackfill=01, triggerMailboxSession=02, activateMailbox=03.
 */
export const INTERNAL_BACKFILL_CODES = {
  /** runBackfill=01 (`POST /internal/backfill/:table`). */
  Backfill: {
    /** `:table` isn't one of the closed `dns-security` | `warmup-score` | `domain-age` values. */
    InvalidTable: '220101',
  },
  /** activateMailbox=03 (`POST /internal/mailbox-activation/:accountId`). */
  MailboxActivation: {
    /**
     * A partner-managed mailbox (`partnerIntegrationId` set — the partner
     * refreshes its tokens). Imported credentials would be stored but never
     * used. DWD mailboxes are supported via `credentialType: 'dwd'`.
     */
    UnsupportedAccount: '220301',
    /** The encrypted token write to `emailAccountCredentials` threw. */
    CredentialsSaveFailed: '220302',
    /** The delayed `reconcile-one` job couldn't be added to Redis. */
    SessionEnqueueFailed: '220303',
    /** OAuth account (`authMethod = OAUTH`) but `accessToken`/`refreshToken`/`expiresIn` not all sent. */
    MissingOAuthTokens: '220304',
    /** Password account (`authMethod = PASSWORD`) but the required SMTP/IMAP fields not all sent. */
    MissingSmtpFields: '220305',
    /**
     * `credentialType` doesn't match the account: `dwd` ↔ DWD-imported
     * (`dwdWorkspaceId` set), otherwise `oauth` ↔ OAUTH, `smtp` ↔ PASSWORD.
     */
    CredentialTypeMismatch: '220306',
    /**
     * The body carries fields of a set `credentialType` doesn't name — both
     * sets, or any credential field on a `dwd` call.
     */
    MixedCredentialSets: '220307',
    /**
     * DWD mailbox: `TokenService` couldn't mint an access token for it — the
     * Google service account / Microsoft app in this environment isn't the one
     * the customer's admin authorized, or the grant was revoked. Nothing written.
     */
    DwdTokenMintFailed: '220308',
  },
} as const;

export const INTERNAL_BACKFILL_CATALOG: Record<string, CatalogEntry> = {
  '220101': {
    httpStatus: 400,
    message: "That table isn't supported. Use dns-security, warmup-score, or domain-age.",
  },
  '220301': {
    httpStatus: 400,
    message: 'This mailbox is managed by a partner, so it cannot take imported credentials.',
  },
  '220302': {
    httpStatus: 500,
    message: "We couldn't save the mailbox credentials. Nothing else was changed — retry the call.",
  },
  '220303': {
    httpStatus: 500,
    message: "Warmup is on, but we couldn't queue the mailbox session. It will start on the next 15-minute sweep.",
  },
  '220304': {
    httpStatus: 400,
    message: 'This mailbox connects with OAuth. Send accessToken, refreshToken and expiresIn.',
  },
  '220305': {
    httpStatus: 400,
    message:
      'This mailbox connects with a password. Send smtpHost, smtpPort, smtpEncryption, smtpUsername, password, ' +
      'imapHost, imapPort and imapEncryption.',
  },
  '220306': {
    httpStatus: 400,
    message:
      "credentialType doesn't match how this mailbox connects. Use dwd for a domain-wide delegation mailbox, " +
      'oauth for an OAuth mailbox and smtp for a password one.',
  },
  '220307': {
    httpStatus: 400,
    message: 'Send only the credentials credentialType names — OAuth tokens or SMTP/IMAP fields, never both, and none for dwd.',
  },
  '220308': {
    httpStatus: 502,
    message:
      "We couldn't get an access token for this domain-wide delegation mailbox. Check that the workspace admin " +
      'still authorizes our app. Nothing was changed.',
  },
};
